import { Crown, Eye, Shield, UserCircle, UserCog, ArrowUpRight } from "lucide-react";
import { getRoleLabel, ROLES } from "../../utils/roles";

const ROLE_STYLE = {
  [ROLES.OWNER]: { icon: Crown, bg: "from-amber-50 to-orange-50 border-amber-200 text-amber-800" },
  [ROLES.ADMIN]: { icon: Shield, bg: "from-purple-50 to-fuchsia-50 border-purple-200 text-purple-700" },
  [ROLES.MODERATOR]: { icon: UserCog, bg: "from-blue-50 to-cyan-50 border-blue-200 text-blue-700" },
  [ROLES.VIEWER]: { icon: Eye, bg: "from-slate-50 to-slate-100 border-slate-200 text-slate-700" },
};

export function RoleBadge({ role, size = "md" }) {
  const roleStyle = ROLE_STYLE[role] || {
    icon: UserCircle,
    bg: "from-slate-50 to-slate-100 border-slate-200 text-slate-700",
  };
  const Icon = roleStyle.icon;
  const isSmall = size === "sm";

  return (
    <div
      className={`inline-flex items-center gap-2 bg-gradient-to-r ${roleStyle.bg} border rounded-xl ${
        isSmall ? "px-2 py-1" : "px-3.5 py-2"
      }`}
    >
      <Icon size={isSmall ? 13 : 16} />
      <span className={`font-bold ${isSmall ? "text-xs" : "text-sm"}`}>
        {getRoleLabel(role, { short: isSmall })}
      </span>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="animate-pulse rounded-xl border border-white/10 bg-white/10 p-3">
      <div className="mb-2 h-5 w-3/5 rounded bg-white/20" />
      <div className="h-4 w-4/5 rounded bg-white/15" />
    </div>
  );
}

export function SummaryCard({ item, onClick, options = {} }) {
  const { showAmount = true, showPieces = false, compact = false } = options;
  const showPieceCount = showPieces || item.showPieces;
  const isLoading = item.loading;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        group relative flex flex-col justify-between overflow-hidden rounded-xl border border-white/20
        bg-gradient-to-br ${item.color} text-right text-white shadow-md shadow-black/5
        transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-black/15 active:scale-[0.985]
        ${compact ? "p-3" : "p-3.5"}
        ${item.prominent ? "min-h-[148px] items-center text-center md:min-h-[168px]" : "min-h-[96px] md:min-h-[108px]"}
        ${item.highlight && (item.data?.count ?? 0) > 0 ? "shadow-lg shadow-rose-900/25 ring-4 ring-white/80" : ""}
      `}
    >
      <div className="pointer-events-none absolute -left-6 -top-6 h-20 w-20 rounded-full bg-white/10 blur-xl transition-transform duration-700 group-hover:scale-150" />
      <h3 className={`relative font-bold leading-snug ${item.prominent ? "text-sm tracking-wide md:text-base" : "text-sm md:text-lg"}`}>
        {item.title}
      </h3>
      {isLoading ? (
        <SkeletonCard />
      ) : (
        <div className="relative mt-2">
          <div className="flex items-end gap-1.5">
            <p className="text-xl font-extrabold leading-none tracking-tight md:text-3xl">
              {item.data?.count ?? 0}
            </p>
            <span className="pb-0.5 text-sm opacity-90">{item.unit || "طلب"}</span>
          </div>
          {showPieceCount && (
            <div className="mt-2 border-t border-white/20 pt-2">
              <p className="text-xs opacity-80">عدد القطع</p>
              <p className="text-base font-bold">{(item.data?.totalProductsSold ?? 0).toLocaleString()}</p>
            </div>
          )}
          {showAmount && (
            <div className="mt-2 border-t border-white/20 pt-2">
              <p className="text-xs opacity-80">إجمالي المبلغ</p>
              <p className="text-base font-bold">
                {(item.data?.totalAmount ?? 0).toLocaleString()} <span className="text-xs opacity-80">ج.م</span>
              </p>
            </div>
          )}
        </div>
      )}
      <span className="absolute left-2 top-2 rounded-full bg-white/20 p-1 opacity-0 transition group-hover:opacity-100">
        <ArrowUpRight size={12} />
      </span>
    </button>
  );
}
